<template>
  <div class="settings-block">
    <div
      v-if="!groupChatNotificationsEnabled"
      class="warning-box"
      style="
        margin-bottom: 16px;
        padding: 12px;
        background: #fff3cd;
        border: 1px solid #ffc107;
        border-radius: 4px;
      "
    >
      <div style="display: flex; align-items: flex-start; gap: 8px">
        <span style="font-size: 18px">⚠️</span>
        <div>
          <strong style="display: block; margin-bottom: 4px">{{
            s__('ProjectSettings|Group chat notifications are disabled')
          }}</strong>
          <div style="font-size: 13px; color: #856404">
            {{
              s__(
                'ProjectSettings|Group chat notifications are currently disabled for this project. Enable them in Basic Settings to use these preferences.'
              )
            }}
          </div>
        </div>
      </div>
    </div>
    <div class="info-block">
      <div class="info-block-header">
        <span class="info-icon"><InfoIcon /></span>
        <span class="info-title">{{ s__('ProjectSettings|Notification guide') }}</span>
      </div>
      <div class="info-block-content">
        <p>
          {{
            s__(
              'ProjectSettings|Control which events trigger group chat notifications. Note: all events will still send direct messages.'
            )
          }}
        </p>
      </div>
    </div>

    <AlertBanner
      v-if="alert"
      :type="alert.type"
      :title="alert.title"
      :message="alert.message"
      @dismiss="alert = null"
    />
    <div v-if="loading" class="muted">
      {{ s__('ProjectSettings|Loading...') }}
    </div>
    <div v-else>
      <div class="mobile-underlined-checklist">
        <div
          v-for="eventType in commonEventTypes"
          :key="eventType.value"
          class="notification-preference-row mobile-underlined-checklist-item"
        >
          <label style="display: flex; align-items: center; gap: 8px; cursor: pointer">
            <input
              type="checkbox"
              :checked="isEventEnabled(eventType.value)"
              :disabled="saving || autoSaving"
              @change="toggleEvent(eventType.value, $event.target.checked)"
            />
            <span>{{ eventType.label }}</span>
          </label>
        </div>
      </div>
      <div v-if="!showAdvancedEvents" class="show-more-container">
        <button
          type="button"
          class="show-more-btn"
          :disabled="saving || autoSaving"
          @click="showAdvancedEvents = true"
        >
          {{ s__('ProjectSettings|Show more events') }}
        </button>
      </div>
      <div v-if="showAdvancedEvents">
        <div class="mobile-underlined-checklist">
          <div
            v-for="eventType in advancedEventTypes"
            :key="eventType.value"
            class="notification-preference-row mobile-underlined-checklist-item"
          >
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer">
              <input
                type="checkbox"
                :checked="isEventEnabled(eventType.value)"
                :disabled="saving || autoSaving"
                @change="toggleEvent(eventType.value, $event.target.checked)"
              />
              <span>{{ eventType.label }}</span>
            </label>
          </div>
        </div>
        <div class="show-more-container">
          <button
            type="button"
            class="show-more-btn"
            :disabled="saving || autoSaving"
            @click="showAdvancedEvents = false"
          >
            {{ s__('ProjectSettings|Show fewer events') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useMutation, useLazyQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__ } from '@/locale'
import AlertBanner from '../common/AlertBanner.vue'
import InfoIcon from '../common/icons/InfoIcon.vue'
import '../../styles/mobile-underlined-checklist.css'

const props = defineProps({
  projectId: String,
  canUpdateProject: {
    type: Boolean,
    default: false,
  },
  groupChatNotificationsEnabled: {
    type: Boolean,
    default: true,
  },
})

// Event types that can be controlled at project level
// Common events (shown by default)
const commonEventTypes = [
  { value: 'created', label: s__('ProjectSettings|Task created') },
  { value: 'status_changed', label: s__('ProjectSettings|Status changed') },
  { value: 'completed', label: s__('ProjectSettings|Task completed') },
  { value: 'label_attached', label: s__('ProjectSettings|Label attached') },
  { value: 'label_detached', label: s__('ProjectSettings|Label detached') },
  { value: 'comment_added', label: s__('ProjectSettings|Comment added') },
]

// Advanced events (shown when "Show more events" is clicked)
const advancedEventTypes = [
  { value: 'task_title_updated', label: s__('ProjectSettings|Title updated') },
  {
    value: 'task_description_updated',
    label: s__('ProjectSettings|Description updated'),
  },
  {
    value: 'task_priority_updated',
    label: s__('ProjectSettings|Priority updated'),
  },
  {
    value: 'task_due_at_updated',
    label: s__('ProjectSettings|Due date updated'),
  },
  {
    value: 'task_assignee_id_updated',
    label: s__('ProjectSettings|Assignee changed'),
  },
  {
    value: 'task_confidential_updated',
    label: s__('ProjectSettings|Confidentiality changed'),
  },
  {
    value: 'task_archived_updated',
    label: s__('ProjectSettings|Archive status changed'),
  },
  {
    value: 'horizontal_relation_created',
    label: s__('ProjectSettings|Task relation created'),
  },
  {
    value: 'horizontal_relation_updated',
    label: s__('ProjectSettings|Task relation updated'),
  },
  {
    value: 'horizontal_relation_destroyed',
    label: s__('ProjectSettings|Task relation removed'),
  },
  {
    value: 'vertical_relation_created',
    label: s__('ProjectSettings|Task hierarchy created'),
  },
  {
    value: 'vertical_relation_destroyed',
    label: s__('ProjectSettings|Task hierarchy removed'),
  },
  {
    value: 'attachment_uploaded',
    label: s__('ProjectSettings|Attachment uploaded'),
  },
  {
    value: 'attachment_destroyed',
    label: s__('ProjectSettings|Attachment removed'),
  },
]

const loading = ref(true)
const saving = ref(false)
const autoSaving = ref(false)
const alert = ref(null)
const notificationPreferences = ref({})
const originalPreferences = ref({})
const pendingChanges = ref({})
const showAdvancedEvents = ref(false)
const formInitialized = ref(false)
const isPopulatingForm = ref(false)
const autoSaveTimeout = ref(null)

const PROJECT_QUERY = gql`
  query ProjectNotificationPreferences($id: ID!) {
    project(id: $id) {
      id
      notificationPreferences
    }
  }
`

const UPDATE_NOTIFICATION_PREFERENCES = gql`
  mutation UpdateProjectNotificationPreferences($id: ID!, $notificationPreferences: JSON!) {
    updateProjectNotificationPreferences(
      id: $id
      notificationPreferences: $notificationPreferences
    ) {
      project {
        id
        notificationPreferences
      }
      errors
    }
  }
`

// Lazy load notification preferences when component is mounted (which happens when section expands)
const {
  load: loadNotificationPreferences,
  result,
  loading: qLoading,
} = useLazyQuery(PROJECT_QUERY, () => ({
  id: props.projectId,
}))

const { mutate: updateNotificationPreferencesMutation } = useMutation(
  UPDATE_NOTIFICATION_PREFERENCES
)

// Load data when component is mounted (component only mounts when section is expanded due to v-show)
onMounted(() => {
  loadNotificationPreferences()
})

watch(
  qLoading,
  val => {
    loading.value = val
  },
  { immediate: true }
)
watch(
  result,
  data => {
    if (data?.project) {
      isPopulatingForm.value = true
      notificationPreferences.value = data.project.notificationPreferences || {}
      originalPreferences.value = JSON.parse(JSON.stringify(notificationPreferences.value))
      pendingChanges.value = {}
      formInitialized.value = true

      // Check if any advanced event is enabled - if so, show advanced events by default
      const hasAdvancedEventEnabled = advancedEventTypes.some(eventType => {
        const preference = notificationPreferences.value[eventType.value]
        return preference !== false // enabled if not explicitly false
      })
      if (hasAdvancedEventEnabled) {
        showAdvancedEvents.value = true
      }

      // Reset populating flag after a short delay to allow watchers to settle
      setTimeout(() => {
        isPopulatingForm.value = false
      }, 100)
    }
  },
  { immediate: true }
)

const hasChanges = computed(() => {
  return Object.keys(pendingChanges.value).length > 0
})

function isEventEnabled(eventType) {
  // If there's a pending change, use that
  if (pendingChanges.value.hasOwnProperty(eventType)) {
    return pendingChanges.value[eventType] === true
  }
  // Otherwise check the current preferences (defaults to true if not set)
  const preference = notificationPreferences.value[eventType]
  return preference !== false
}

function toggleEvent(eventType, enabled) {
  // If enabling an advanced event, automatically show the advanced events list
  if (enabled && advancedEventTypes.some(et => et.value === eventType)) {
    showAdvancedEvents.value = true
  }

  // If the new value matches the original, remove from pending changes
  const originalValue = originalPreferences.value[eventType]
  const wouldBeDefault = originalValue === undefined && enabled === true

  if (wouldBeDefault || originalValue === enabled) {
    // Remove from pending changes
    const { [eventType]: _, ...rest } = pendingChanges.value
    pendingChanges.value = rest
  } else {
    // Add to pending changes
    pendingChanges.value = {
      ...pendingChanges.value,
      [eventType]: enabled,
    }
  }

  // Auto-save if form is initialized
  if (formInitialized.value && !isPopulatingForm.value) {
    if (autoSaveTimeout.value) {
      clearTimeout(autoSaveTimeout.value)
    }
    autoSaveTimeout.value = setTimeout(() => {
      autoSaveNotificationPreferences()
    }, 500)
  }
}

// Auto-save function for notification preferences
async function autoSaveNotificationPreferences() {
  if (!props.projectId || autoSaving.value || saving.value) return

  // Only save if form has been initialized (project data loaded)
  if (!formInitialized.value) return

  // Don't auto-save if we're currently populating the form
  if (isPopulatingForm.value) return

  // Don't auto-save if there are no changes
  if (Object.keys(pendingChanges.value).length === 0) return

  autoSaving.value = true
  alert.value = null

  try {
    // Build the preferences object to send
    // The service merges with existing preferences, so we send only the changes
    // For enabled events (true), we send null to remove the preference (defaults to enabled)
    // For disabled events (false), we send false to explicitly disable
    const preferencesToSave = {}

    Object.keys(pendingChanges.value).forEach(eventType => {
      const value = pendingChanges.value[eventType]
      if (value === true) {
        // Setting to true means removing the preference (default behavior)
        // Send null to clear it
        preferencesToSave[eventType] = null
      } else {
        // Setting to false means explicitly disabling
        preferencesToSave[eventType] = false
      }
    })

    const { data } = await updateNotificationPreferencesMutation({
      id: props.projectId,
      notificationPreferences: preferencesToSave,
    })

    if (data?.updateProjectNotificationPreferences?.errors?.length > 0) {
      alert.value = {
        type: 'error',
        title: s__('ProjectSettings|Error'),
        message: data.updateProjectNotificationPreferences.errors.join(', '),
      }
    } else if (data?.updateProjectNotificationPreferences?.project) {
      // Update local state
      notificationPreferences.value =
        data.updateProjectNotificationPreferences.project.notificationPreferences || {}
      originalPreferences.value = JSON.parse(JSON.stringify(notificationPreferences.value))
      pendingChanges.value = {}

      alert.value = {
        type: 'success',
        title: s__('ProjectSettings|Success'),
        message: s__('ProjectSettings|Preferences saved successfully!'),
      }
    }
  } catch (err) {
    alert.value = {
      type: 'error',
      title: s__('ProjectSettings|Error'),
      message: err.message || s__('ProjectSettings|Failed to save notification preferences'),
    }
  } finally {
    autoSaving.value = false
  }
}
</script>

<style scoped>
.info-block {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
  background: #f8f9fa;
}

.info-block-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f1f5f9;
}

.info-icon {
  font-size: 16px;
  display: inline-flex;
  color: #2aabee;
}

.info-title {
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}

.info-block-content {
  padding: 10px 12px;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}

.info-block-content p {
  margin: 0;
}

@media (min-width: 768px) {
  .notification-preference-row {
    padding: 8px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .notification-preference-row:last-child {
    border-bottom: none;
  }
}

.notification-preference-row label {
  user-select: none;
}

.notification-preference-row input[type='checkbox'] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.notification-preference-row span {
  font-size: 14px;
  color: #333;
}

.show-more-container {
  margin-top: 12px;
  padding-top: 12px;
}
</style>
